"use client";

import { useParams } from "next/navigation";
import { Locale } from "@/utils/i18n";
import { MontserratText, WindsongText } from "@/components/Typography";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
interface FooterProps {
  footer: any;
}

export default function Footer({ footer }: FooterProps) {
  const params = useParams();
  const locale = (params?.locale as Locale) || "vi";

  return (
    <>
      <footer
        id="main-footer"
        className="w-full relative text-white z-40  overflow-hidden -mt-8 md:-mt-12"
        style={{
          backgroundImage: "url('/images/background_footer_main.svg')",
          backgroundSize: "cover",
        }}
      >
        <div className="absolute inset-0 bg-black/40 pointer-events-none z-0" />
        <div className="relative z-10 px-6 py-12 md:py-16 md:px-[100px] flex flex-col items-center gap-8 md:gap-10">
          {/* Logo & Partner Section */}
          <div className="w-full flex flex-col md:flex-row items-center justify-center gap-10 md:gap-24 max-w-[1100px]">
            {/* Left Column: Industrial Cluster Logo */}
            <div className="flex flex-col items-center text-center gap-2">
              <div className="h-[36px] sm:h-[48px] md:h-[62px] lg:h-[72px] flex items-center justify-center transition-all duration-300 hover:scale-105">
                <img
                  src="/logos/ccn_logo.svg"
                  alt="CCN Logo"
                  loading="lazy"
                  className="brightness-0 invert opacity-90 h-full w-auto object-contain"
                />
              </div>
              <MontserratText
                mobile="7px"
                sm="8px"
                md="9px"
                lg="10px"
                xl="11px"
                className="text-white font-bold  tracking-[0.15em] uppercase mt-2"
              >
                {locale === "vi"
                  ? "Cụm Công Nghiệp Tây Bắc Đức Hòa Đông"
                  : locale === "en"
                    ? "Tay Bac Duc Hoa Dong Industrial Cluster"
                    : "西北德和东 industrial cluster"}
              </MontserratText>
            </div>

            {/* Right Column: ZenGroup Logo */}
            {/* <div className="flex flex-col items-center text-center gap-2">
              <a
                href="https://zengroup.com.vn/"
                target="_blank"
                rel="noopener noreferrer"
                className="h-[36px] sm:h-[48px] md:h-[62px] lg:h-[72px] flex items-center justify-center transition-all duration-300 hover:scale-105 cursor-pointer"
              >
                <img
                  src="/logos/ZenGroup_Logo.svg"
                  alt="ZenGroup Logo"
                  loading="lazy"
                  className="brightness-0 invert opacity-90 h-full w-auto object-contain"
                />
              </a>
              <MontserratText
                mobile="7px"
                sm="8px"
                md="9px"
                lg="11px"
                xl="11px"
                className="text-white font-bold  tracking-[0.15em] uppercase mt-2"
              >
                {locale === "vi"
                  ? "Công ty Cổ Phần Tập đoàn ZenGroup"
                  : locale === "en"
                    ? "ZENGROUP CORPORATION"
                    : "ZENGROUP CORPORATION"}
              </MontserratText>
            </div> */}
          </div>

          {/* Slogan */}
          <div className="w-full flex justify-center mt-2 mb-2 select-none">
            <WindsongText className="text-[26px] sm:text-[34px] md:text-[42px] lg:text-[48px] text-center normal-case">
              {locale === "vi"
                ? "Mở không gian - Dẫn tăng trưởng"
                : locale === "en"
                  ? "Open Space - Drive Growth"
                  : "开辟空间 - 引领增长"}
            </WindsongText>
          </div>

          {/* Contact Details Row/Column */}
          <div className="w-full max-w-[1200px] flex flex-col lg:flex-row justify-center items-center gap-y-3 lg:gap-y-0 gap-x-4 xl:gap-x-6 leading-relaxed">
            {/* Address */}
            <div className="text-center lg:text-left">
              <MontserratText
                mobile="12px"
                sm="12px"
                md="12px"
                lg="11px"
                xl="11px"
                className="text-white/60 font-medium text-center lg:text-left"
              >
                {locale === "vi"
                  ? "Địa chỉ: "
                  : locale === "en"
                    ? "Address: "
                    : "地址: "}
                <span className="text-white font-bold ml-1">
                  {footer.address}
                </span>
              </MontserratText>
            </div>

            {/* Divider 1 */}
            <div className="hidden lg:block w-[1px] h-3.5 bg-white/20 self-center" />

            {/* Email */}
            <div className="text-center lg:text-left">
              <div className="flex flex-row items-center justify-center lg:justify-start">
                <MontserratText
                  mobile="12px"
                  sm="12px"
                  md="12px"
                  lg="11px"
                  xl="11px"
                  className="text-white/60 font-medium whitespace-nowrap"
                >
                  {"Email: "}
                </MontserratText>
                <div className="flex flex-col items-start ml-1.5 leading-tight">
                  <MontserratText
                    mobile="12px"
                    sm="12px"
                    md="12px"
                    lg="11px"
                    xl="11px"
                    className="text-white font-bold text-left"
                  >
                    {footer.email || "contactzentaland@gmail.com"}
                  </MontserratText>
                </div>
              </div>
            </div>

            {/* Divider 2 */}
            <div className="hidden lg:block w-[1px] h-3.5 bg-white/20 self-center" />

            {/* Hotline */}
            <div className="text-center lg:text-left">
              <MontserratText
                mobile="12px"
                sm="12px"
                md="12px"
                lg="11px"
                xl="11px"
                className="text-white/60 font-semibold text-center lg:text-left"
              >
                {"HotLine: "}
                <span className="text-white font-bold ml-1">
                  {formatPhoneNumber(footer.phone || "0937 657 968")}
                </span>
              </MontserratText>
            </div>
          </div>
        </div>
      </footer>

      {/* Copyright Teal Bar */}
      <div className="w-full bg-[#0c9894] py-2 flex justify-center items-center border-t border-white/10">
        <MontserratText
          mobile="10px"
          sm="10px"
          md="10px"
          lg="11px"
          xl="11px"
          className="font-bold text-white/90 tracking-wider text-center"
        >
          © 2026 CÔNG TY CỔ PHẦN ĐẦU TƯ THƯƠNG MẠI NGỌC PHƯƠNG ĐÔNG. All Rights
          Reserved.
        </MontserratText>
      </div>
    </>
  );
}
